import { useState, useEffect, useCallback } from "react";

// ============================================================================
// brioCAST Revenue Detail Center
// Enhanced: Payout history, Flash-Buy reconciliation, CSV export
// ============================================================================
const API_BASE = "https://api.briolabs.io/api/account";
const MESH_API = "https://api.briolabs.io/api/mesh";

const t = {
  bg: "#0F1117", card: "#1A1D27", cardHover: "#22262F", border: "#2A2E3A",
  accent: "#6C5CE7", accentLight: "#A29BFE", success: "#00B894",
  warning: "#FDCB6E", danger: "#E17055", text: "#E8E8ED",
  textMuted: "#8B8FA3", textDim: "#5A5E72",
};

async function api(path) {
  const token = localStorage.getItem("biz_token");
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return res.json();
}

async function meshApi(path) {
  const token = localStorage.getItem("biz_token");
  const res = await fetch(`${MESH_API}${path}`, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return res.json();
}

function TabBtn({ active, label, onClick, icon }) {
  return (
    <button onClick={onClick} style={{
      padding: "10px 16px", fontSize: 13, fontWeight: active ? 600 : 400,
      border: `1px solid ${active ? t.accent : t.border}`, borderRadius: 8,
      background: active ? `${t.accent}18` : "transparent",
      color: active ? t.accentLight : t.textMuted, cursor: "pointer",
    }}>{icon} {label}</button>
  );
}

function Card({ children, style }) {
  return <div style={{ background: t.card, borderRadius: 12, border: `1px solid ${t.border}`, padding: 20, marginBottom: 12, ...style }}>{children}</div>;
}

function Stat({ label, value, color, sub }) {
  return (
    <div style={{ flex: "1 1 160px", minWidth: 130 }}>
      <div style={{ fontSize: 12, color: t.textDim, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || t.text }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: t.textDim, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── CSV Export helper ──
function exportCSV(filename, headers, rows) {
  const csv = [headers.join(","), ...rows.map(r => r.map(c => `"${c}"`).join(","))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB 1: AD REVENUE OVERVIEW
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function AdRevenueTab({ account }) {
  const [data, setData] = useState(null);
  const [mesh, setMesh] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api("/revenue/summary").catch(() => ({ data: {} })),
      meshApi(`/revenue?from=${new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10)}&to=${new Date().toISOString().slice(0, 10)}`).catch(() => null),
    ]).then(([rev, m]) => {
      setData(rev.data || {});
      setMesh(m);
      setLoading(false);
    });
  }, [account]);

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: t.textDim }}>載入中...</div>;

  const d = data || {};
  const meshSummary = mesh?.summary || {};

  return (
    <div>
      {/* Summary Cards */}
      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: 18 }}>📺</span>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: t.text }}>廣告收益總覽</h3>
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Stat label="本月收益" value={`NT$${(d.currentMonth || 0).toLocaleString()}`} color={t.accent} />
          <Stat label="上月收益" value={`NT$${(d.lastMonth || 0).toLocaleString()}`} />
          <Stat label="累計總收益" value={`NT$${(d.allTime || 0).toLocaleString()}`} color={t.success} />
          <Stat label="可提領餘額" value={`NT$${(d.availableBalance || 0).toLocaleString()}`} color={t.warning} sub="每月25日申請" />
        </div>
      </Card>

      {/* Mesh Analytics */}
      {meshSummary.total_impressions > 0 && (
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 12 }}>📊 30 天曝光分析</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Stat label="總曝光" value={(meshSummary.total_impressions || 0).toLocaleString()} />
            <Stat label="總 GMV" value={`NT$${parseFloat(meshSummary.total_gmv || 0).toLocaleString()}`} color={t.success} />
            <Stat label="轉換率" value={`${meshSummary.conversion_rate_pct || 0}%`} color={t.accentLight} />
            <Stat label="Fill Rate" value={`${meshSummary.fill_rate_pct || 100}%`} />
          </div>
        </Card>
      )}

      {/* Daily Trend */}
      {mesh?.breakdown?.length > 0 && (
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 12 }}>每日趨勢</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 80 }}>
            {mesh.breakdown.map((d, i) => {
              const maxImp = Math.max(...mesh.breakdown.map(x => parseInt(x.impressions) || 0), 1);
              const h = Math.max(4, (parseInt(d.impressions) || 0) / maxImp * 70);
              return (
                <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <div style={{ width: "100%", maxWidth: 16, height: h, background: t.accent, borderRadius: 2 }} />
                  <div style={{ fontSize: 8, color: t.textDim, marginTop: 2 }}>{(d.day || "").slice(5, 10)}</div>
                </div>
              );
            })}
          </div>
          <div style={{ textAlign: "right", marginTop: 8 }}>
            <button onClick={() => {
              exportCSV(`revenue_daily_${new Date().toISOString().slice(0, 10)}.csv`,
                ["日期", "曝光", "GMV", "訂單"],
                mesh.breakdown.map(d => [d.day?.slice(0, 10), d.impressions, d.gmv, d.orders])
              );
            }} style={{ padding: "4px 12px", borderRadius: 6, border: `1px solid ${t.border}`, background: "transparent", color: t.textMuted, fontSize: 11, cursor: "pointer" }}>
              📥 匯出 CSV
            </button>
          </div>
        </Card>
      )}

      {/* Withdrawal */}
      <Card style={{ borderColor: `${t.success}30` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: t.text }}>💰 提領申請</div>
            <div style={{ fontSize: 11, color: t.textDim, marginTop: 4 }}>
              已提領：NT${(d.totalWithdrawn || 0).toLocaleString()} · 最低 NT$1,000 · Flash-Buy 交易款由 PSP 自動撥付
            </div>
          </div>
          <button style={{
            padding: "10px 20px", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer",
            background: (d.availableBalance || 0) >= 1000 ? t.accent : t.border,
            color: (d.availableBalance || 0) >= 1000 ? "#fff" : t.textDim,
            opacity: (d.availableBalance || 0) >= 1000 ? 1 : 0.5,
          }}>
            申請提領
          </button>
        </div>
      </Card>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB 2: PAYOUT HISTORY
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function PayoutHistoryTab({ account }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api("/revenue/history").then(d => {
      setHistory(d.data?.history || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [account]);

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: t.textDim }}>載入中...</div>;

  const statusColor = { paid: t.success, pending: t.warning, processing: t.accentLight, failed: t.danger };
  const statusLabel = { paid: "已撥付", pending: "待處理", processing: "處理中", failed: "失敗" };

  return (
    <div>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: t.text }}>📋 撥款紀錄</h3>
          {history.length > 0 && (
            <button onClick={() => {
              exportCSV(`payout_history_${new Date().toISOString().slice(0, 10)}.csv`,
                ["日期", "期間", "類型", "金額(NT$)", "狀態"],
                history.map(h => [h.paid_at || h.created_at, h.period || "", h.type || "ad_revenue", h.amount, h.status])
              );
            }} style={{ padding: "4px 12px", borderRadius: 6, border: `1px solid ${t.border}`, background: "transparent", color: t.textMuted, fontSize: 11, cursor: "pointer" }}>
              📥 匯出
            </button>
          )}
        </div>

        {history.length === 0 ? (
          <div style={{ textAlign: "center", padding: 32, color: t.textDim }}>
            <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }}>📭</div>
            <div>尚無撥款紀錄</div>
          </div>
        ) : (
          <div>
            {history.map((h, i) => (
              <div key={i} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "12px 0", borderBottom: i < history.length - 1 ? `1px solid ${t.border}` : "none",
              }}>
                <div>
                  <div style={{ fontSize: 13, color: t.text, fontWeight: 500 }}>
                    {h.period || (h.paid_at || h.created_at || "").slice(0, 10)}
                  </div>
                  <div style={{ fontSize: 11, color: t.textDim, marginTop: 2 }}>
                    {h.type === "flash_buy" ? "🛒 Flash-Buy 交易款" : "📺 廣告分潤"}
                    {h.invoice_id && ` · #${h.invoice_id}`}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: t.text }}>NT${(h.amount || 0).toLocaleString()}</div>
                  <div style={{
                    fontSize: 10, fontWeight: 600, marginTop: 2,
                    color: statusColor[h.status] || t.textDim,
                  }}>
                    {statusLabel[h.status] || h.status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB 3: FLASH-BUY RECONCILIATION
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function FlashBuyReconciliationTab({ account }) {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("7");

  const load = useCallback(() => {
    setLoading(true);
    api(`/orders?days=${period}`).then(d => {
      const txns = d.data?.orders || d.data?.transactions || [];
      setTransactions(txns);

      // Calculate summary
      const paid = txns.filter(t => t.status === "paid" || t.status === "completed" || t.status === "success");
      const total = paid.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
      const platformFee = Math.round(total * 0.03 * 100) / 100;
      setSummary({
        totalOrders: txns.length,
        paidOrders: paid.length,
        gmv: total,
        platformFee,
        netPayout: Math.round((total - platformFee) * 100) / 100,
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [period]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: t.textDim }}>載入中...</div>;

  const statusColor = { pending: t.warning, paid: t.success, completed: t.success, success: t.success, failed: t.danger, refunded: t.textDim };

  return (
    <div>
      {/* Period */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        {[["7", "7天"], ["14", "14天"], ["30", "30天"], ["90", "90天"]].map(([v, label]) => (
          <button key={v} onClick={() => setPeriod(v)} style={{
            padding: "6px 14px", borderRadius: 6, fontSize: 12, cursor: "pointer",
            border: `1px solid ${period === v ? t.accent : t.border}`,
            background: period === v ? `${t.accent}22` : "transparent",
            color: period === v ? t.accentLight : t.textMuted,
          }}>{label}</button>
        ))}
      </div>

      {/* Summary */}
      <Card>
        <div style={{ fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 12 }}>🛒 Flash-Buy 對帳摘要</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Stat label="總訂單" value={summary.totalOrders || 0} />
          <Stat label="已付款" value={summary.paidOrders || 0} color={t.success} />
          <Stat label="GMV" value={`NT$${(summary.gmv || 0).toLocaleString()}`} color={t.accent} />
          <Stat label="平台費 (3%)" value={`NT$${(summary.platformFee || 0).toLocaleString()}`} color={t.danger} />
          <Stat label="淨撥付" value={`NT$${(summary.netPayout || 0).toLocaleString()}`} color={t.success} />
        </div>
      </Card>

      {/* Transaction List */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: t.text }}>交易明細 ({transactions.length} 筆)</h3>
          {transactions.length > 0 && (
            <button onClick={() => {
              exportCSV(`flashbuy_reconciliation_${new Date().toISOString().slice(0, 10)}.csv`,
                ["交易ID", "日期", "品名", "金額(NT$)", "狀態", "付款方式"],
                transactions.map(tx => [tx.id, tx.created_at?.slice(0, 19), tx.title || tx.offer_id, tx.amount, tx.status, tx.payment_method || "ecpay"])
              );
            }} style={{ padding: "4px 12px", borderRadius: 6, border: `1px solid ${t.border}`, background: "transparent", color: t.textMuted, fontSize: 11, cursor: "pointer" }}>
              📥 匯出 CSV
            </button>
          )}
        </div>

        {transactions.length === 0 ? (
          <div style={{ textAlign: "center", padding: 32, color: t.textDim }}>
            <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }}>📋</div>
            <div>此期間無 Flash-Buy 交易</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                  {["交易 ID", "日期", "品名", "金額", "狀態"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: t.textDim, fontSize: 11, fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {transactions.slice(0, 50).map((tx, i) => (
                  <tr key={tx.id || i} style={{ borderBottom: `1px solid ${t.border}08` }}
                    onMouseEnter={e => e.currentTarget.style.background = t.cardHover}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "8px 10px", fontFamily: "monospace", fontSize: 11, color: t.textMuted }}>
                      {(tx.id || "").slice(0, 16)}
                    </td>
                    <td style={{ padding: "8px 10px", color: t.textDim, fontSize: 11 }}>
                      {(tx.created_at || tx.paid_at || "").slice(0, 16).replace("T", " ")}
                    </td>
                    <td style={{ padding: "8px 10px", color: t.text }}>
                      {tx.title || tx.offer_title || tx.offer_id || "—"}
                    </td>
                    <td style={{ padding: "8px 10px", fontWeight: 600, color: t.text }}>
                      NT${(tx.amount || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: "8px 10px" }}>
                      <span style={{
                        display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 600,
                        color: statusColor[tx.status] || t.textDim,
                        background: `${statusColor[tx.status] || t.textDim}15`,
                      }}>
                        {tx.status === "paid" || tx.status === "success" ? "✓ 已付款" :
                          tx.status === "completed" ? "✓ 已完成" :
                          tx.status === "pending" ? "⏳ 待付款" :
                          tx.status === "refunded" ? "↩ 已退款" :
                          tx.status === "failed" ? "✗ 失敗" : tx.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {transactions.length > 50 && (
              <div style={{ padding: 12, textAlign: "center", color: t.textDim, fontSize: 12 }}>
                顯示前 50 筆，共 {transactions.length} 筆。請匯出 CSV 查看完整明細。
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN PAGE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export default function RevenueDetailPage({ account }) {
  const [tab, setTab] = useState("overview");

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: t.text, margin: "0 0 4px" }}>💰 收益中心</h2>
        <p style={{ fontSize: 13, color: t.textDim, margin: 0 }}>廣告分潤 · 撥款紀錄 · Flash-Buy 對帳</p>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        <TabBtn active={tab === "overview"} icon="📺" label="廣告收益" onClick={() => setTab("overview")} />
        <TabBtn active={tab === "payout"} icon="📋" label="撥款紀錄" onClick={() => setTab("payout")} />
        <TabBtn active={tab === "flashbuy"} icon="🛒" label="Flash-Buy 對帳" onClick={() => setTab("flashbuy")} />
      </div>

      {tab === "overview" && <AdRevenueTab account={account} />}
      {tab === "payout" && <PayoutHistoryTab account={account} />}
      {tab === "flashbuy" && <FlashBuyReconciliationTab account={account} />}
    </div>
  );
}
